export const volumes = [
  {
    numeral: 'I',
    title: 'Start With the Functional Concern',
    color: '#2F8F6B',
    soft: '#E7F5EE',
    intro: [
      'Before exploring possible sensory explanations, you need to understand the actual problem you’re trying to investigate.',
      'In this volume, I’ll show you how to organize the initial concern without immediately assigning a sensory interpretation.',
    ],
    listLabel: 'You’ll learn to structure:',
    items: [
      'The family’s main concern.',
      'The child’s functional difficulties.',
      'Activities and routines affected.',
      'Environmental and contextual factors.',
      'Relevant background information.',
      'Initial behavioral observations.',
      'Questions that need clarification.',
    ],
    ordered: false,
    quote: '',
    note: '',
    formula: [] as string[],
    goal: 'The goal: define what you’re trying to understand before deciding what the observations might mean.',
  },
  {
    numeral: 'II',
    title: 'The Sensory Modulation Observation Map',
    color: '#3566B8',
    soft: '#E8EFFB',
    intro: [
      'Observing a behavior is one thing. Organizing the information surrounding that behavior is another.',
      'This volume gives you a visual framework for recording observations and exploring their relationship with sensory modulation.',
    ],
    listLabel: 'You’ll receive:',
    items: [
      'Sensory modulation observation maps.',
      'Context and activity worksheets.',
      'Behavioral observation prompts.',
      'Environmental factor checklists.',
      'Observation comparison tables.',
      'Questions for further exploration.',
      'Visual reference charts.',
    ],
    ordered: false,
    quote: 'What exactly am I observing, and what information do I need before interpreting it?',
    note: '',
    formula: [] as string[],
    goal: 'The purpose isn’t to classify a child automatically. It’s to help you organize observations and identify relevant questions.',
  },
  {
    numeral: 'III',
    title: 'Explore Possible Patterns Without Jumping to Conclusions',
    color: '#7A4FC4',
    soft: '#F1EBFB',
    intro: [
      'One of the most challenging parts of clinical reasoning is deciding how much meaning to assign to a particular observation.',
      'A behavior can have multiple possible explanations. Context matters, and additional information may change your interpretation.',
    ],
    listLabel: 'In this volume, I’ll help you organize your exploration of:',
    items: [
      'Repeated observations across situations.',
      'Differences between contexts.',
      'Possible patterns of sensory modulation.',
      'Environmental and activity-related factors.',
      'Alternative explanations.',
      'Information that supports or challenges a working hypothesis.',
      'Questions that remain unanswered.',
    ],
    ordered: false,
    quote: '',
    note: 'You’ll receive comparison matrices, reasoning prompts, and visual frameworks to help you organize these elements.',
    formula: [] as string[],
    goal: 'The goal: explore possible patterns systematically without treating individual behaviors as proof of a specific sensory mechanism.',
  },
  {
    numeral: 'IV',
    title: 'The Sensory Modulation Clinical Reasoning Matrix™',
    color: '#D2693A',
    soft: '#FCEEE6',
    intro: [
      'This is where the different pieces of information come together.',
      'Instead of treating every observation as an isolated event, you’ll use a structured matrix to organize the relationships between observations, context, functional impact, and possible interpretations.',
    ],
    listLabel: 'You’ll learn to organize:',
    items: [
      'The functional concern.',
      'Observable behaviors.',
      'Relevant contexts.',
      'Possible sensory modulation questions.',
      'Missing information.',
      'Alternative hypotheses.',
      'Supporting and conflicting information.',
      'Next questions to investigate.',
    ],
    ordered: false,
    quote: '',
    note: 'You’ll also receive the Sensory Modulation Decision Matrix™, a visual worksheet designed to make your reasoning process easier to review.',
    formula: [] as string[],
    goal: 'The purpose isn’t to produce an automatic answer. It’s to help you see what your current information supports, what remains uncertain, and what deserves further exploration.',
  },
  {
    numeral: 'V',
    title: 'From Working Hypothesis to Functional Goals',
    color: '#5E8A3A',
    soft: '#EEF5E6',
    intro: [
      'Organizing observations is only part of the process. You also need to understand how the concern affects the child’s everyday participation.',
      'In this volume, I’ll show you how to structure the connection between your case analysis and functional goals.',
    ],
    listLabel: 'You’ll explore:',
    items: [
      'Functional participation.',
      'Meaningful daily activities.',
      'Observable changes.',
      'Context-specific goals.',
      'Baseline information.',
      'Progress documentation.',
      'Goal review frameworks.',
      'Communication of functional outcomes.',
    ],
    ordered: false,
    quote: '',
    note: 'You’ll use a practical structure:',
    formula: ['Function', 'Context', 'Observable Change', 'Measurement'],
    goal: 'Instead of relying on vague statements such as “improve sensory processing,” you’ll have a framework for organizing goals around meaningful activities and observable outcomes.',
  },
  {
    numeral: 'VI',
    title: 'The Complete Sensory Modulation Clinical Pathway™',
    color: '#2F6FA8',
    soft: '#E6F0F8',
    intro: ['This final volume brings everything together in one repeatable educational workflow.'],
    listLabel: 'You’ll learn how to move through eight stages:',
    items: [
      'Define the functional concern.',
      'Organize relevant background information.',
      'Document observable behaviors.',
      'Explore context and possible patterns.',
      'Identify missing information.',
      'Formulate and review working hypotheses.',
      'Connect the analysis with functional goals.',
      'Identify the next steps for investigation and follow-up.',
    ],
    ordered: true,
    quote: '',
    note: 'You’ll also learn to recognize when the available information is insufficient to support a stronger conclusion.',
    formula: [] as string[],
    goal: 'The purpose is to help you approach sensory modulation questions with a more organized process instead of relying on memory alone.',
  },
];
